import type { AttackRejectReason, PlayerState } from "@ict/shared";
import { t } from "@/i18n/i18n";

export interface TargetMarkerInfo {
  screenX: number;
  screenY: number;
  visible: boolean;
  distanceMeters: number;
  label: string;
}

/** In-game HUD: mission progress, target marker, player status row, combat feedback, crosshair. */
export class HUD {
  private root: HTMLDivElement;
  private progressValue: HTMLDivElement;
  private targetMarker: HTMLDivElement;
  private rejectHost: HTMLDivElement;
  private playerStatusRow: HTMLDivElement;
  private missionCompleteEl: HTMLDivElement;
  private bottomLeft: HTMLDivElement;

  constructor(container: HTMLElement) {
    this.root = document.createElement("div");
    this.root.id = "hud";
    this.root.innerHTML = `
      <div class="crosshair"></div>
      <div class="hud-top">
        <div class="hud-progress-label">${t("hud.missionProgress")}</div>
        <div class="hud-progress-value">0 / 0</div>
      </div>
      <div class="hud-player-status"></div>
      <div class="hud-target-marker hidden"></div>
      <div class="hud-reject-host"></div>
      <div class="hud-mission-complete hidden">
        <div class="line1">${t("hud.missionComplete")}</div>
        <div class="line2">${t("hud.waitingForOthers")}</div>
      </div>
      <div class="hud-bottom-left"></div>
    `;
    container.appendChild(this.root);

    this.progressValue = this.root.querySelector(".hud-progress-value")!;
    this.targetMarker = this.root.querySelector(".hud-target-marker")!;
    this.rejectHost = this.root.querySelector(".hud-reject-host")!;
    this.playerStatusRow = this.root.querySelector(".hud-player-status")!;
    this.missionCompleteEl = this.root.querySelector(".hud-mission-complete")!;
    this.bottomLeft = this.root.querySelector(".hud-bottom-left")!;
  }

  setVisible(visible: boolean): void {
    this.root.classList.toggle("hidden", !visible);
  }

  setProgress(completed: number, total: number): void {
    this.progressValue.textContent = `${completed} / ${total}`;
  }

  setPlayerStatuses(players: PlayerState[]): void {
    this.playerStatusRow.innerHTML = players
      .map((p) => `<span class="${p.missionComplete ? "done" : ""}">${escapeHtml(p.name)} ${p.missionComplete ? "✓" : "..."}</span>`)
      .join("");
  }

  updateTargetMarker(info: TargetMarkerInfo | null): void {
    if (!info || !info.visible) {
      this.targetMarker.classList.add("hidden");
      return;
    }
    this.targetMarker.classList.remove("hidden");
    this.targetMarker.style.left = `${info.screenX}px`;
    this.targetMarker.style.top = `${info.screenY}px`;
    this.targetMarker.innerHTML = `${escapeHtml(info.label)}<span class="dist">${Math.round(info.distanceMeters)}${t("hud.meters")}</span>`;
  }

  showBanner(text: string, durationMs = 1800): void {
    const el = document.createElement("div");
    el.className = "hud-banner";
    el.textContent = text;
    this.rejectHost.appendChild(el);
    window.setTimeout(() => el.remove(), durationMs);
  }

  showReject(reason: AttackRejectReason): void {
    const el = document.createElement("div");
    el.className = "hud-reject";
    el.textContent = t(`reject.${reason}`);
    this.rejectHost.appendChild(el);
    window.setTimeout(() => el.remove(), 950);
  }

  showMissionComplete(): void {
    this.missionCompleteEl.classList.remove("hidden");
  }

  hideMissionComplete(): void {
    this.missionCompleteEl.classList.add("hidden");
  }

  setBottomLeft(text: string): void {
    this.bottomLeft.textContent = text;
  }

  dispose(): void {
    this.root.remove();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
